<template>
  <div v-if="chartData">
    <line-chart
      :data="chartData"
      :options="chartOptions"
      :plugins="chartPlugins"
    />
    <div id="legend-container" />
  </div>
  <div
    v-else
    class="h-32 flex items-center justify-center"
  >
    <span class="text-grey-001">No Data Found</span>
  </div>
</template>

<script>
import {debounce} from "lodash";
import moment from "moment";

import LineChart from "@/components/lineChart";
import {tooltipConfig} from "@/utils/chartUtils";
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";

export default {
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  components: {
    LineChart
  },
  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val.length) {
          const labels = Array
            .from({ length: 12 }, (_, i) => this.moment().month(i).format('MMM'))
            .map((month) => month.toUpperCase());

          const arrOfMonths = Array
            .from({ length: 12 }, (_, i) => this.moment().month(i).format('MM'))
          const operationTimeData = arrOfMonths
            .map((month) => {
              const monthDataExists = this.data.find(item => item.month === month );
              if (monthDataExists) {
                return monthDataExists.avg_operation_time_days
              }
              return 0
            });

          const cycleTimeData = arrOfMonths
            .map((month) => {
              const monthDataExists = this.data.find(item => item.month === month );
              if (monthDataExists) {
                return monthDataExists.avg_cycle_time_days
              }
              return 0
            });

          this.chartData = {
            labels: labels,
            datasets: [
              {
                label: 'Operation Time',
                data: operationTimeData,
                backgroundColor: '#004479',
                borderColor: '#004479',
                pointRadius: 0,
                pointBorderWidth: 0,
                tension: 0.4,
                hitRadius: 10
              },
              {
                label: 'Cycle Time',
                data: cycleTimeData,
                backgroundColor: '#7451D0',
                borderColor: '#7451D0',
                fill: {
                  target: '-1',
                  above: '#FFD9D9'
                },
                pointRadius: 0,
                pointBorderWidth: 0,
                tension: 0.4,
                hitRadius: 10
              },
              {
                label: 'Delay Time',
                data: cycleTimeData.map((item, i) => item - operationTimeData[i]),
                backgroundColor: '#FFD9D9',
                borderColor: 'transparent',
                pointRadius: 0,
                pointBorderWidth: 0,
                yAxisID: 'y-delay-time'
              }
            ]
          }
        } else {
          this.chartData = null
        }
      }
    }
  },
  computed: {
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            left: 25,
            top: 30,
            right: 25
          }
        },
        scales: {
          x: {
            offset: true,
            grid: {
              display: false
            },
            ticks: {
              color: 'black',
              font: {
                family: 'Poppins'
              }
            }
          },
          y: {
            offset: true,
            grid: {
              display: false
            },
            stacked: true,
            ticks: {
              color: 'black',
              font: {
                family: 'Poppins'
              }
            }
          },
          'y-delay-time': {
            display: false,
            offset: false,
            grid: {
              display: false
            },
            stacked: false,
            ticks: {
              display: false,
              drawTicks: false
            },
            border: {
              display: false
            }
          }
        },
        onHover: debounce(function (event, activeEls, chart) {
          const points = chart.getElementsAtEventForMode(event, 'index', { intersect: false }, false)
          if (!points.length) return

          const index = points[0].index
          const cycleMeta = chart.getDatasetMeta(1) // 'Cycle Time'
          const operationMeta = chart.getDatasetMeta(0) // 'Operation Time'

          const cyclePoint = cycleMeta.data[index]
          const operationPoint = operationMeta.data[index]

          if (!cyclePoint || !operationPoint) return

          const upperY = Math.min(cyclePoint.y, operationPoint.y)
          const lowerY = Math.max(cyclePoint.y, operationPoint.y)

          const mouseY = event.native.offsetY

          const isInsideFillArea = mouseY > upperY + 10 && mouseY < lowerY - 10

          const tooltip = chart.tooltip
          if (isInsideFillArea) {
            if (!tooltip.active) {
              tooltip.setActiveElements(
                [
                  {
                    datasetIndex: 2,
                    index: index
                  }
                ],
                {
                  x: event.native.offsetX,
                  y: event.native.offsetY
                }
              )
              tooltip.options['x'] = event.native.offsetX
              tooltip.options['y'] = event.native.offsetY
              tooltip.update('none')
            }
          } else {
            const { datasetIndex } = tooltip.getActiveElements()
            if (datasetIndex === 2) {
              tooltip.setActiveElements([])
              tooltip.update('none')
            }
          }
        }, 75),
        plugins: {
          filler: {
            propagate: false
          },
          'samples-filler-analyser': {
            target: 'chart-analyser'
          },
          legend: {
            display: false
          },
          chartAreaBorder: {
            borderColor: 'black',
            borderWidth: 2
          },
          customTitle: {
            y: {
              display: true,
              text: 'Days',
              color: '#000000',
              font: {
                family: 'Poppins',
                size: 12
              }
            }
          },
          axisIcons: true,
          htmlLegend: {
            containerID: 'legend-container'
          },
          labels: [
            {
              text: 'Operation Time'
            },
            {
              text: 'Cycle Time'
            },
            {
              text: 'Delay Times'
            }
          ],
          tooltip: tooltipConfig({
            mode: 'nearest',
            intersect: true,
            position: 'nearest',
            callbacks: {
              title(titleItems) {
                const month = titleItems[0]?.label
                return moment(month, 'MMM').format('MMMM').toUpperCase()
              },
              label(tooltipItem) {
                const { raw, dataset: { label } } = tooltipItem
                const parsed = handleValidateAndFormatNumber(raw)
                return `${ label } average: ${parsed} days`
              }
            }
          })
        }
      }
    },
    chartPlugins() {
      return [
        this.chartAreaBorderPlugin(),
        this.customTitlePlugin(),
        this.axisIconPlugin(),
        this.htmlLegendPlugin(),
        this.tooltipAreaBetweenLine()
      ]
    }
  },
  data() {
    return {
      chartData: null
    }
  },
  methods: {
    chartAreaBorderPlugin() {
      return {
        id: 'chartAreaBorder',
        beforeDraw(chart, args, options) {
          const {ctx, chartArea: {left, top, width, height}} = chart
          const extension = 15

          ctx.save();
          ctx.strokeStyle = options.borderColor || 'black'
          ctx.lineWidth = options.borderWidth || 2

          ctx.beginPath();
          ctx.moveTo(left, top - extension)
          ctx.lineTo(left, top + height)
          ctx.stroke()

          ctx.beginPath()
          ctx.moveTo(left, top + height)
          ctx.lineTo(left + width, top + height)
          ctx.stroke();

          ctx.restore()
        }
      }
    },
    customTitlePlugin() {
      return {
        id: 'customTitle',
        afterDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, width } } = chart

          if (options?.y?.display) {
            ctx.save()
            ctx.fillStyle = options.y.color || 'black'

            const font = options.y.font || {}
            const fontSize = font.size || 10;
            const fontFamily = font.family || 'sans-serif'
            const fontWeight = font.weight || 'normal'
            ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`

            ctx.textAlign = 'center'
            ctx.textBaseline = 'top'

            const xPos = left / 2
            const yPos = top - 25

            ctx.fillText(options.y.text, xPos, yPos)
            ctx.restore()
          }
        }
      }
    },
    axisIconPlugin() {
      return {
        id: 'axisIcons',
        afterDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, right, bottom }, scales } = chart;

          const topIcon = {
            text: '\u2BC5',
            x: left,
            y: top - 15,
            color: '#000',
            font: 'bold 20px FontAwesome'
          };

          const rightIcon = {
            text: '\u2BC8',
            x: right,
            y: bottom + 2,
            color: '#000',
            font: 'bold 20px Poppins'
          }

          ctx.save()
          ctx.font = topIcon.font
          ctx.fillStyle = topIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(topIcon.text, topIcon.x, topIcon.y)
          ctx.restore();

          ctx.save()
          ctx.font = rightIcon.font
          ctx.fillStyle = rightIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(rightIcon.text, rightIcon.x, rightIcon.y)
          ctx.restore()
        }
      }
    },
    htmlLegendPlugin() {
      return {
        id: 'htmlLegend',
        getOrCreateLegendList(chart, id) {
          const legendContainer = document.getElementById(id);
          let listContainer = legendContainer.querySelector('ul');

          if (!listContainer) {
            listContainer = document.createElement('ul');
            listContainer.style.display = 'flex';
            listContainer.style.flexDirection = 'row';
            listContainer.style.justifyContent = 'center';
            listContainer.style.margin = '1rem';
            listContainer.style.padding = 0;

            legendContainer.appendChild(listContainer);
          }

          return listContainer;
        },
        afterUpdate(chart, args, options) {
          const ul = this.getOrCreateLegendList(chart, options.containerID);

          // Remove old legend items
          while (ul.firstChild) {
            ul.firstChild.remove();
          }

          // Reuse the built-in legendItems generator
          const items = chart.options.plugins.legend.labels.generateLabels(chart);

          items.forEach(item => {
            const li = document.createElement('li');
            li.style.alignItems = 'center';
            li.style.display = 'flex';
            li.style.flexDirection = 'row';
            li.style.marginLeft = '20px';
            li.style.gap = '5px';

            // Color box
            const boxSpan = document.createElement('span');
            boxSpan.style.background = item.fillStyle;
            boxSpan.style.borderColor = item.strokeStyle;
            boxSpan.style.borderWidth = `${item.lineWidth}px`
            boxSpan.style.display = 'inline-block';
            boxSpan.style.flexShrink = 0;
            boxSpan.style.height = '10px';
            boxSpan.style.marginRight = '0px';
            boxSpan.style.width = '34px';
            boxSpan.style.borderRadius = '5px';

            // Text
            const textContainer = document.createElement('p');
            textContainer.style.color = item.fontColor;
            textContainer.style.margin = 0;
            textContainer.style.padding = 0;
            textContainer.style.textDecoration = item.hidden ? 'line-through' : '';
            textContainer.style.fontSize = '12px'

            const text = document.createTextNode(item.text);
            textContainer.appendChild(text);

            li.appendChild(boxSpan);
            li.appendChild(textContainer);
            ul.appendChild(li);
          });
        }
      }
    },
    tooltipAreaBetweenLine() {
      return {
        id: 'tooltipArea',
        beforeTooltipDraw (chart, args) {
          const tooltip = chart.tooltip
          const { dataPoints } = tooltip
          const isDelayTooltipActive = dataPoints?.length && dataPoints[0]?.datasetIndex === 2
          if (isDelayTooltipActive) {
            const { options: { x, y }, height } = args.tooltip
            tooltip.x = x
            tooltip.y = y - height / 2
          }
        }
      }
    }
  }
}
</script>

<style lang="scss"  scoped>
</style>
